/**
 * @fileoverview Skill Executor - スキル実行ルーター
 */

import { MinimalErrorTracker } from '../../shared/log.js';

import { getSkillDefinition, getAllSkillTypes } from './definitions.js';
import * as boardHandler from './handlers/board-handler.js';
import * as specialHandler from './handlers/special-handler.js';
// 将来: import * as timerHandler from './handlers/timer-handler.js';
// 将来: import * as movesHandler from './handlers/moves-handler.js';

/**
 * @typedef {Object} SkillResult
 * @property {boolean} ok - 成功/失敗
 * @property {string} [reason] - 失敗理由
 * @property {Error} [error] - エラーオブジェクト
 */

/**
 * カテゴリ別ハンドラマッピング
 */
const HANDLERS = {
  'special-piece': specialHandler,
  board: boardHandler,
  // 将来: 'timer': timerHandler,
  // 将来: 'moves': movesHandler,
};

/**
 * スキル効果を実行する統一関数
 *
 * @param {string} skillType - スキルタイプ
 * @param {Object|null} target - ターゲット座標 { row, col } または null
 * @returns {Promise<SkillResult>}
 */
export async function executeSkillEffect(skillType, target) {
  try {
    const def = getSkillDefinition(skillType);
    if (!def) {
      MinimalErrorTracker.warn('SKILL', `Unknown skill type: ${skillType}`);
      return { ok: false, reason: 'unknown_skill' };
    }

    if (def.requiresTarget && !target) {
      MinimalErrorTracker.warn('SKILL', `Target required for skill: ${skillType}`);
      return { ok: false, reason: 'invalid_target' };
    }

    const handler = HANDLERS[def.category];
    if (!handler) {
      MinimalErrorTracker.warn('SKILL', `Unknown category: ${def.category}`);
      return { ok: false, reason: 'unknown_category' };
    }

    // アニメーション（オプション）
    if (handler.scatterPieces) {
      await handler.scatterPieces(def, target);
    } else if (handler.showEffect) {
      await handler.showEffect(def, target);
    }

    // 実行
    return await handler.execute(def, target);
  } catch (error) {
    MinimalErrorTracker.error('SKILL', 'Skill execution error', error);
    return { ok: false, reason: 'execution_error', error };
  }
}

/**
 * 利用可能なスキルタイプ一覧を取得
 * @returns {string[]} スキルタイプ配列
 */
export function getAvailableSkillTypes() {
  return getAllSkillTypes();
}
